import type { RichTextContentProtocol, TextBlockAbstract, TextProtocol, VariableADTvalue } from '@lighthouse/core'
import { BACKGROUND_TYPE } from '@lighthouse/core'
import type { RichTextEditorProps } from '@lighthouse/shared'
import {
    applyFillPickerValue4CssColorValue,
    fieldConvertValue,
    flexMinSizeStyle,
    getTextColorStyle,
    RichTextContentCss,
    RichTextRenderer,
    TEXT_FONT_NORMAL,
    useApplicationContext,
    useFillPickerContext,
    useFlowLayoutContext,
    useMediaQueryRemBase,
    useMultipleFloatBoxBlockContext
} from '@lighthouse/shared'
import React, { memo, useCallback, useMemo } from 'react'
import styled, { css } from 'styled-components'

import { AnimationDecorators } from '../../components'
import { useEffectiveAnchorId } from '../../context'
import { useCustomViewBlockContext } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'

const TEXT_PROTOCOL_SET = new Set<string>(['lineClamp'] satisfies (keyof TextProtocol)[])

const TextContainer = styled(AnimationDecorators).withConfig<TextProtocol>({
    shouldForwardProp: p => !TEXT_PROTOCOL_SET.has(p as string)
})`
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    padding: var(--block-padding);

    .ProseMirror,
    .ProseMirror > * {
        ${({ lineClamp }) => {
            if (!lineClamp) {
                return
            }
            return lineClamp === 1
                ? css`
                      overflow: hidden;
                      text-overflow: ellipsis;
                      white-space: nowrap;
                  `
                : css`
                      display: -webkit-box;
                      -webkit-box-orient: vertical;
                      -webkit-line-clamp: ${lineClamp};
                      overflow: hidden;
                  `
        }}
    }
`

const StyledHtmlValue = styled.div`
    ${RichTextContentCss}
`

const hasLatex = (value: RichTextContentProtocol | null): boolean => {
    if (!value) {
        return false
    }

    // 检查当前节点是否为数学公式节点
    if (value.type === 'inlineMath' || value.type === 'blockMath') {
        return true
    }

    // 递归检查子节点
    if (value.content && Array.isArray(value.content)) {
        return value.content.some(child => hasLatex(child))
    }

    return false
}

interface RichTextBlockProps extends React.ComponentPropsWithoutRef<'div'> {
    config?: RichTextEditorProps['config']
    blockData: TextBlockAbstract
    resolveVariable?: (v: VariableADTvalue) => string
}

const TextBlock: React.FC<RichTextBlockProps> = memo(({ config: richtextConfig, blockData, resolveVariable, ...rest }) => {
    const {
        id,
        title,
        config: {
            content: { value, htmlValue },
            breakPoint,
            anchor
        }
    } = blockData

    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
    const { font, design, size, animation } = breakPoint
    const { color, shadow, textBorder } = design
    const { align, fontFamily, fontStyle, fontWeight, fontSize, lineHeight, letterSpacing, lineClamp } = font

    const { isBuilder } = useApplicationContext()
    const { palettes } = useFillPickerContext()
    const { interactionProps, reference } = useMultipleFloatBoxBlockContext()

    // ################################# 解析颜色变量 #################################
    const { useParseVariableWithSubscription } = useFlowLayoutContext()
    const { record } = useCustomViewBlockContext()

    const parseColorVariable = useParseVariableWithSubscription(color?.variable)
    const parseColor = useCallback(
        (v: VariableADTvalue) => {
            const field = parseColorVariable(v, { viewRecord: record })
            if (!field) {
                return ''
            }

            return fieldConvertValue(field)
        },
        [parseColorVariable, record]
    )
    // ################################# 解析颜色变量 #################################

    const borderStyles = useMemo(() => {
        if (!textBorder) {
            return
        }

        let borderColor: string | undefined
        if (textBorder.type === BACKGROUND_TYPE.variable) {
            borderColor = textBorder.variable && parseColor(textBorder.variable)
        } else {
            borderColor = applyFillPickerValue4CssColorValue(textBorder.color, palettes)
        }

        if (!borderColor) {
            return
        }

        return { WebkitTextStrokeColor: borderColor, WebkitTextStrokeWidth: textBorder.all ?? 1 } satisfies React.CSSProperties
    }, [textBorder, parseColor, palettes])

    const shadowStyles = useMemo(() => {
        if (!shadow || !shadow.color) {
            return
        }

        const shadowColor = applyFillPickerValue4CssColorValue(shadow.color, palettes)

        return { textShadow: `${shadow.x || 0}px ${shadow.y || 0}px ${shadow.blur || 0}px ${shadowColor}` } satisfies React.CSSProperties
    }, [palettes, shadow])

    // ################################# 字体样式 #################################
    const rootFontSize = useMediaQueryRemBase()

    const fs = useMemo(() => {
        if (!fontSize || !fontSize.size) {
            return
        }

        let rawFontSize
        if (fontSize?.unit === 'rem' && isBuilder) {
            rawFontSize = `${rootFontSize * (fontSize.size || 0)}px`
        } else {
            rawFontSize = `${fontSize.size}${fontSize?.unit || 'px'}`
        }

        return rawFontSize
    }, [fontSize, isBuilder, rootFontSize])

    const fontStyles = useMemo(() => {
        return {
            fontFamily: fontFamily === TEXT_FONT_NORMAL ? undefined : fontFamily,
            fontStyle,
            fontWeight,
            fontSize: fs,
            letterSpacing: `${letterSpacing ?? 0}em`,
            lineHeight: `${lineHeight ?? 1}`
        }
    }, [fontFamily, fontStyle, fontWeight, fs, letterSpacing, lineHeight])
    // ################################# 字体样式 #################################

    const style = useMemo(
        () => ({
            ...flexMinSizeStyle,
            textAlign: align,
            ...getTextColorStyle(color, palettes, parseColor),
            ...borderStyles,
            ...shadowStyles,
            ...fontStyles
        }),
        [align, borderStyles, color, fontStyles, palettes, shadowStyles, parseColor]
    )

    // 快速路径：htmlValue 已预编译好结构，只需替换变量占位符
    const resolvedHtmlValue = useMemo(() => {
        if (!htmlValue) return null
        if (!htmlValue.includes('<variable') || !resolveVariable) return htmlValue

        const doc = new DOMParser().parseFromString(`<div>${htmlValue}</div>`, 'text/html')
        const container = doc.body.firstElementChild!

        container.querySelectorAll<HTMLElement>('variable[data-value]').forEach(el => {
            try {
                const variableADT = JSON.parse(el.getAttribute('data-value')!) as VariableADTvalue
                el.replaceWith(resolveVariable(variableADT))
            } catch {
                el.replaceWith('')
            }
        })

        return container.innerHTML
    }, [htmlValue, resolveVariable])

    return (
        <TextContainer
            blockId={id}
            blockName={title}
            animation={animation}
            id={effectiveAnchorId}
            lineClamp={lineClamp}
            ref={reference}
            {...interactionProps.getReferenceProps({ ...rest, style: { ...rest.style, overflow: size.overflow } })}
        >
            {resolvedHtmlValue != null ? (
                <StyledHtmlValue className="tiptap ProseMirror" style={style}>
                    <div className="tiptap ProseMirror" dangerouslySetInnerHTML={{ __html: resolvedHtmlValue }} />
                </StyledHtmlValue>
            ) : (
                <RichTextRenderer value={value} config={richtextConfig} style={style} />
            )}
        </TextContainer>
    )
})

TextBlock.displayName = 'TextBlock'

export default TextBlock
